import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plane, Ship, Truck, Package, Search, Globe, Star, Container, Download, Mail as CourierIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/money";
import jsPDF from "jspdf";
import { ensureArabicFont, pdfText, hasArabic } from "@/lib/pdfFonts";
import { PdfLangMenu } from "@/components/PdfLangMenu";
import { findCountry, countryLabel, provinceLabel } from "@/components/GeoSelect";

export default function Quote() {
  const { api } = useAuth();
  const { t, lang } = useI18n();

  const PDF_STR = {
    es: { subtitle: "Cotización de envío", origin: "ORIGEN", destination: "DESTINO", mode: "Modo", cargo: "Carga", days_transit: "días de tránsito", route: "Ruta", price_breakdown: "DESGLOSE DE PRECIO", base_price: "Precio base", subtotal: "Subtotal", margin: "Margen", waybill_fee: "Cargo por guía", total: "TOTAL", observations: "OBSERVACIONES", footer: "Meos Cargo · Plataforma profesional de envíos" },
    en: { subtitle: "Shipping quote", origin: "ORIGIN", destination: "DESTINATION", mode: "Mode", cargo: "Cargo", days_transit: "days of transit", route: "Route", price_breakdown: "PRICE BREAKDOWN", base_price: "Base price", subtotal: "Subtotal", margin: "Margin", waybill_fee: "Waybill fee", total: "TOTAL", observations: "NOTES", footer: "Meos Cargo · Professional shipping platform" },
    ar: { subtitle: "عرض سعر شحن", origin: "المصدر", destination: "الوجهة", mode: "الوسيلة", cargo: "الحمولة", days_transit: "يوم عبور", route: "المسار", price_breakdown: "تفاصيل السعر", base_price: "السعر الأساسي", subtotal: "المجموع الفرعي", margin: "الهامش", waybill_fee: "رسوم الوثيقة", total: "المجموع", observations: "ملاحظات", footer: "Meos Cargo · منصة شحن احترافية" },
  };

  const generatePDF = async (r, meta, merch, langOverride = null) => {
    const activeLang = langOverride || lang;
    const S = PDF_STR[activeLang] || PDF_STR.en;
    const surchargeAmt = merch && merch.surcharge ? +merch.surcharge : 0;
    const displayedTotal = +(r.precio || 0) + surchargeAmt;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40;

    // Preload Arabic font if AR selected or any dynamic field has Arabic
    if (activeLang === "ar" || [meta.origen, meta.destino, r.carrier, r.servicio, r.observaciones].some(hasArabic)) {
      await ensureArabicFont(doc);
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { try { doc.addImage(img, "PNG", M, 30, 90, 60); } catch (e) { console.warn("Quote logo failed:", e); } render(); };
      img.onerror = render;
      img.src = "/logo.png";

      function render() {
        // ==== HEADER ====
        doc.setFont("helvetica","bold"); doc.setFontSize(20); doc.setTextColor(185,28,28);
        doc.text("MEOS CARGO", W - M, 55, { align: "right" });
        doc.setFontSize(10); doc.setFont("helvetica","normal"); doc.setTextColor(100,116,139);
        pdfText(doc, S.subtitle, W - M, 72, { align: "right" });
        doc.setFontSize(8); doc.setTextColor(148,163,184);
        doc.text(new Date().toLocaleString(), W - M, 88, { align: "right" });

        // Red band separator
        let y = 110;
        doc.setFillColor(185, 28, 28); doc.rect(M, y, W - M*2, 3, "F");

        // ==== ROUTE BOX ====
        y += 20;
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(M, y, W - M*2, 60, 6, 6, "F");
        doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(120,120,120);
        pdfText(doc, S.origin, M + 15, y + 18);
        pdfText(doc, S.destination, W/2 + 20, y + 18);
        doc.setFontSize(14); doc.setTextColor(15,23,42);
        pdfText(doc, meta.origen, M + 15, y + 40);
        pdfText(doc, meta.destino, W/2 + 20, y + 40);
        // Arrow
        doc.setDrawColor(185,28,28); doc.setLineWidth(2);
        doc.line(W/2 - 15, y + 34, W/2 + 15, y + 34);
        doc.line(W/2 + 15, y + 34, W/2 + 8, y + 28);
        doc.line(W/2 + 15, y + 34, W/2 + 8, y + 40);

        y += 78;
        doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(80,80,80);
        pdfText(doc, `${S.mode}: ${(meta.mode||"").toUpperCase()}   |   ${S.cargo}: ${(meta.tipo_carga||"").toUpperCase()}`, M, y);

        // ==== CARRIER ====
        y += 26;
        doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.5);
        doc.line(M, y, W - M, y);
        y += 20;
        doc.setFont("helvetica","bold"); doc.setFontSize(13); doc.setTextColor(15,23,42);
        pdfText(doc, r.carrier, M, y);
        y += 14;
        doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(100,116,139);
        pdfText(doc, `${r.servicio}  ·  ${r.dias} ${S.days_transit}`, M, y);
        if (r.puerto_origen) { y += 12; pdfText(doc, `${S.route}: ${r.puerto_origen}  ->  ${r.puerto_destino}`, M, y); }

        // ==== BREAKDOWN TABLE ====
        y += 30;
        const cur = r.moneda || "USD";
        const d = r.desglose || {};
        doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(15,23,42);
        pdfText(doc, S.price_breakdown, M, y);
        y += 8;
        doc.setFillColor(185, 28, 28); doc.rect(M, y, W - M*2, 2, "F");

        const rows = [
          [S.base_price, fmtMoney(d.precio_base, cur)],
          [`${d.cantidad} x ${fmtMoney(d.precio_unitario, cur)} / ${d.unidad}`, fmtMoney(d.subtotal_variable, cur)],
          [S.subtotal, fmtMoney(d.subtotal, cur)],
          [`${S.margin} (${d.margen_pct}%)`, fmtMoney(d.margen_monto, cur)],
          [S.waybill_fee, fmtMoney(d.cargo_guia, cur)],
        ];
        if (merch && merch.selected) {
          rows.push([`${t("merchandise")}: ${merch.selected}`, fmtMoney(surchargeAmt, cur)]);
        }
        doc.setFont("helvetica","normal"); doc.setFontSize(10);
        rows.forEach(([l, v], i) => {
          y += 22;
          if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(M, y - 14, W - M*2, 20, "F"); }
          doc.setTextColor(71,85,105); pdfText(doc, String(l), M + 10, y);
          doc.setTextColor(15,23,42); doc.setFont("helvetica","bold");
          doc.text(String(v), W - M - 10, y, { align: "right" });
          doc.setFont("helvetica","normal");
        });

        // ==== TOTAL ====
        y += 24;
        doc.setFillColor(185, 28, 28);
        doc.roundedRect(M, y, W - M*2, 40, 6, 6, "F");
        doc.setFont("helvetica","bold"); doc.setFontSize(11); doc.setTextColor(255,255,255);
        pdfText(doc, S.total, M + 15, y + 25);
        doc.setFontSize(18);
        doc.text(`${fmtMoney(displayedTotal, cur)}  ${cur}`, W - M - 15, y + 26, { align: "right" });

        // ==== OBSERVATIONS ====
        if (r.observaciones) {
          y += 60;
          doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(120,120,120);
          pdfText(doc, S.observations, M, y);
          y += 12;
          doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(60,60,60);
          const lines = doc.splitTextToSize(r.observaciones, W - M*2);
          lines.forEach(line => { pdfText(doc, line, M, y); y += 12; });
        }

        // ==== FOOTER ====
        doc.setFontSize(7); doc.setTextColor(148,163,184);
        pdfText(doc, S.footer, W/2, H - 30, { align: "center" });
        doc.setDrawColor(185, 28, 28); doc.setLineWidth(1);
        doc.line(M, H - 45, W - M, H - 45);

        const fname = `MeosCargo_${meta.origen}_${meta.destino}_${r.carrier.replace(/\s+/g, "_")}.pdf`;
        doc.save(fname);
        toast.success("PDF");
        resolve();
      }
    });
  };

  const [mode, setMode] = useState("aereo");
  const [origen, setOrigen] = useState("");
  const [destino, setDestino] = useState("");
  const [puertoOrigen, setPuertoOrigen] = useState("");
  const [puertoDestino, setPuertoDestino] = useState("");
  const [provincia, setProvincia] = useState("");
  const [countries, setCountries] = useState([]);
  const [tipoCarga, setTipoCarga] = useState("kg");
  const [containerType, setContainerType] = useState("20ft");
  const [pesoContenedor, setPesoContenedor] = useState(2500);
  const [pesoMercancia, setPesoMercancia] = useState(20000);
  const [dims, setDims] = useState({ l: 50, a: 40, h: 40 });
  const [peso, setPeso] = useState(25);
  const [cbm, setCbm] = useState(1);
  const [pesoCbm, setPesoCbm] = useState(200);
  const [pallets, setPallets] = useState(1);
  const [hsCode, setHsCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [lookup, setLookup] = useState({ origens: [], destinos: [], puertos_origen: [], puertos_destino: [], pairs: {}, provincias_by_pair: {} });
  // Client-side merchandise selection: { [route_id]: merch_type_name }
  const [merchSel, setMerchSel] = useState({});

  const getMerchInfo = (r) => {
    const list = r.merchandise_types || [];
    if (list.length === 0) return { list: [], required: false, selected: null, surcharge: 0, missing: false };
    const required = list.some(mt => mt.is_mandatory);
    const selectedName = merchSel[r.route_id] || "";
    const match = selectedName ? list.find(mt => (mt.nombre || "").trim().toLowerCase() === selectedName.trim().toLowerCase()) : null;
    // For per-kg extras we must multiply by the chargeable weight used in the quote
    // (backend already returned `merchandise_surcharge` for its own selection, but
    // the client can switch merchandise locally: reproduce the same math here).
    const chargeableWeight = +(r.desglose && r.desglose.peso_total ? r.desglose.peso_total : (r.desglose && r.desglose.cantidad && r.desglose.unidad === "KG" ? r.desglose.cantidad : 0)) || 0;
    let surcharge = 0;
    if (match) {
      const unit = +(match.precio || 0);
      surcharge = (match.tipo === "kilo") ? unit * chargeableWeight : unit;
    }
    return {
      list,
      required,
      selected: match ? match.nombre : null,
      surcharge: +surcharge.toFixed(2),
      missing: required && !match,
    };
  };

  useEffect(() => {
    api.get("/routes/lookup").then(r => setLookup(r.data)).catch(() => {});
    api.get("/countries").then(r => setCountries((r.data || []).filter(c => c.activo))).catch(() => {});
  // eslint-disable-next-line
  }, []);

  // When origen changes, filter destinos to those with routes from that origin
  const destinosDisponibles = origen && lookup.pairs && lookup.pairs[origen]
    ? lookup.pairs[origen]
    : lookup.destinos;

  // Country / province names are stored canonically but displayed in the active language
  const geoLabelIn = (value, lg) => {
    const c = findCountry(countries, value);
    if (c) return countryLabel(c, lg);
    for (const country of countries) {
      const p = (country.provinces || []).find(x => x.name === value);
      if (p) return provinceLabel(p, lg);
    }
    return value;
  };
  const geoLabel = (value) => geoLabelIn(value, lang);

  // Destination provinces the admin offers on the selected route + mandatory flag
  const pairInfo = (lookup.provincias_by_pair || {})[`${origen}|${destino}`] || { provincias: [], required: false };
  const provinciasOfrecidas = pairInfo.provincias || [];
  const provinciaRequired = !!pairInfo.required && provinciasOfrecidas.length > 0;

  useEffect(() => {
    if (provinciasOfrecidas.length > 0 && provincia && !provinciasOfrecidas.includes(provincia)) setProvincia("");
  // eslint-disable-next-line
  }, [origen, destino]);

  const modes = [
    { id: "aereo", label: t("aereo"), Icon: Plane },
    { id: "terrestre", label: t("terrestre"), Icon: Truck },
    { id: "maritimo", label: t("maritimo"), Icon: Ship },
    { id: "courier", label: t("courier"), Icon: CourierIcon },
  ];

  const cargoOptions = mode === "maritimo"
    ? [{ v: "cbm", l: t("cargo_cbm") }, { v: "fcl", l: t("fcl") }, { v: "kg", l: t("cargo_kg") }]
    : mode === "terrestre"
      ? [{ v: "pallets", l: t("cargo_pallets") }, { v: "cbm", l: t("cargo_cbm") }, { v: "kg", l: t("cargo_kg") }]
      : mode === "courier"
        ? [{ v: "documentos", l: t("courier_documentos") }, { v: "pasaporte", l: t("courier_pasaporte") }, { v: "goods", l: t("courier_goods") }]
        : [{ v: "kg", l: t("cargo_kg") }, { v: "cbm", l: t("cargo_cbm") }, { v: "pallets", l: t("cargo_pallets") }];

  React.useEffect(() => {
    setTipoCarga(cargoOptions[0].v);
  // eslint-disable-next-line
  }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!origen || !destino) return toast.error(t("select_option"));
    setBusy(true);
    try {
      const payload = {
        mode,
        origen_pais: origen,
        destino_pais: destino,
        puerto_origen: puertoOrigen,
        puerto_destino: puertoDestino,
        china_city: puertoOrigen && ["Guangzhou","Yiwu"].includes(puertoOrigen) ? puertoOrigen : null,
        tipo_carga: tipoCarga,
        provincia,
        container_type: tipoCarga === "fcl" ? containerType : null,
        peso_contenedor: tipoCarga === "fcl" ? pesoContenedor : 0,
        peso_mercancia: tipoCarga === "fcl" ? pesoMercancia : 0,
        largo: dims.l, ancho: dims.a, alto: dims.h,
        peso: tipoCarga === "cbm" ? pesoCbm : peso,
        cbm: tipoCarga === "cbm" ? cbm : 0,
        pallets: tipoCarga === "pallets" ? pallets : 0,
        hs_code: hsCode,
      };
      const { data } = await api.post("/quote", payload);
      setResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6" data-testid="quote-page">
      <div className="bg-white border border-slate-200 rounded-xl p-5 h-fit xl:sticky xl:top-24">
        <h2 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
          <Search className="w-4 h-4 text-red-600" /> {t("quote_shipping")}
        </h2>

        <div className="mb-4">
          <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("quote_type")}</label>
          <div className="grid grid-cols-3 gap-2">
            {modes.map(m => (
              <button
                key={m.id}
                data-testid={`mode-${m.id}`}
                type="button"
                onClick={() => setMode(m.id)}
                className={`flex flex-col items-center gap-1 p-2.5 border-2 rounded-lg transition-all ${
                  mode === m.id ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 hover:border-slate-400"
                }`}
              >
                <m.Icon className="w-4 h-4" />
                <span className="text-xs font-semibold">{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("origin_country")}</label>
              <select data-testid="q-origen" value={origen} onChange={(e)=>{setOrigen(e.target.value); setDestino("");}} required className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600">
                <option value="">{t("select_option")}</option>
                {lookup.origens.map(p => <option key={p} value={p}>{geoLabel(p)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{t("destination_country")}</label>
              <select data-testid="q-destino" value={destino} onChange={(e)=>setDestino(e.target.value)} required className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600">
                <option value="">{t("select_option")}</option>
                {destinosDisponibles.map(p => <option key={p} value={p}>{geoLabel(p)}</option>)}
              </select>
            </div>
          </div>

          {lookup.origens.length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              {t("no_route_available")}
            </p>
          )}

          {lookup.puertos_origen.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("port_origin")}</label>
                <select data-testid="q-puerto-origen" value={puertoOrigen} onChange={(e)=>setPuertoOrigen(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value="">—</option>
                  {lookup.puertos_origen.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("port_dest")}</label>
                <select data-testid="q-puerto-destino" value={puertoDestino} onChange={(e)=>setPuertoDestino(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value="">—</option>
                  {lookup.puertos_destino.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              {t("dest_province")} {provinciaRequired && <span className="text-red-500">*</span>}
            </label>
            {provinciasOfrecidas.length > 0 ? (
              <select
                data-testid="q-provincia"
                required={provinciaRequired}
                value={provincia}
                onChange={(e)=>setProvincia(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600"
              >
                <option value="">{provinciaRequired ? t("select_option") : `— ${t("optional")} —`}</option>
                {provinciasOfrecidas.map(p => {
                  const pr = (pairInfo.pricing || {})[p];
                  const extra = pr && (pr.recargo_fijo || pr.recargo_unidad || pr.dias_extra)
                    ? ` · +${pr.recargo_fijo || 0}${pr.recargo_unidad ? ` +${pr.recargo_unidad}/u` : ""}${pr.dias_extra ? ` · +${pr.dias_extra}d` : ""}`
                    : "";
                  return <option key={p} value={p}>{geoLabel(p)}{extra}</option>;
                })}
              </select>
            ) : (
              <input
                data-testid="q-provincia"
                value={provincia}
                onChange={(e)=>setProvincia(e.target.value)}
                placeholder={t("province_optional_hint")}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600"
                list="q-prov-list"
              />
            )}
            <datalist id="q-prov-list">
              {(findCountry(countries, destino)?.provinces || findCountry(countries, origen)?.provinces || []).map(p => (
                <option key={p.name} value={p.name}>{provinceLabel(p, lang)}</option>
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">{t("cargo_type")}</label>
            <select data-testid="q-tipo-carga" value={tipoCarga} onChange={(e)=>setTipoCarga(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
              {cargoOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>

          {tipoCarga === "fcl" && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-3">
              <label className="block text-xs font-semibold text-red-900">{t("container_type")}</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "20ft", l: t("container_20") },
                  { v: "40ft", l: t("container_40") },
                  { v: "40hc", l: t("container_40hc") },
                ].map(c => (
                  <button
                    key={c.v}
                    type="button"
                    data-testid={`container-${c.v}`}
                    onClick={() => setContainerType(c.v)}
                    className={`px-2 py-2 border-2 rounded-lg text-xs font-semibold transition-all ${
                      containerType === c.v ? "border-red-600 bg-white text-red-700" : "border-transparent bg-white/50 hover:bg-white"
                    }`}
                  >
                    <Container className="w-4 h-4 mx-auto mb-1" /> {c.l}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-600 mb-1">{t("container_weight")}</label>
                  <input type="number" data-testid="q-peso-contenedor" value={pesoContenedor} onChange={(e)=>setPesoContenedor(+e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white" />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-600 mb-1">{t("cargo_weight")}</label>
                  <input type="number" data-testid="q-peso-mercancia" value={pesoMercancia} onChange={(e)=>setPesoMercancia(+e.target.value)} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white" />
                </div>
              </div>
              <p className="text-xs text-red-800">{t("total_weight_fcl")}: <b className="tabular-nums">{(pesoContenedor + pesoMercancia).toLocaleString()} kg</b></p>
            </div>
          )}

          {(tipoCarga === "kg" || tipoCarga === "pallets") && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="block text-[11px] text-slate-500 mb-1">{t("length")}</label><input type="number" value={dims.l} onChange={(e)=>setDims({...dims,l:+e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" /></div>
                <div><label className="block text-[11px] text-slate-500 mb-1">{t("width")}</label><input type="number" value={dims.a} onChange={(e)=>setDims({...dims,a:+e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" /></div>
                <div><label className="block text-[11px] text-slate-500 mb-1">{t("height")}</label><input type="number" value={dims.h} onChange={(e)=>setDims({...dims,h:+e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm" /></div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("weight_kg")}</label>
                <input type="number" data-testid="q-peso" value={peso} onChange={(e)=>setPeso(+e.target.value)} step="0.1" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              {tipoCarga === "pallets" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("pallets_qty")}</label>
                  <input type="number" data-testid="q-pallets" value={pallets} onChange={(e)=>setPallets(+e.target.value)} min="1" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
              )}
            </>
          )}

          {tipoCarga === "cbm" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("cbm_amount")}</label>
                  <input type="number" data-testid="q-cbm" value={cbm} onChange={(e)=>setCbm(+e.target.value)} step="0.1" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{t("total_weight")}</label>
                  <input type="number" value={pesoCbm} onChange={(e)=>setPesoCbm(+e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("hs_code")}</label>
                <input value={hsCode} onChange={(e)=>setHsCode(e.target.value)} placeholder="8471.30" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
            </>
          )}

          <button
            data-testid="q-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            <Search className="w-4 h-4" /> {busy ? "..." : t("calculate")}
          </button>
        </form>
      </div>

      <div data-testid="q-results">
        {!results ? (
          <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-slate-400">
            <Globe className="w-16 h-16 mx-auto opacity-30 mb-3" />
            <p className="text-lg">{t("empty_quote")}</p>
            <p className="text-sm mt-1">{t("empty_quote_sub")}</p>
          </div>
        ) : results.sin_ruta ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-12 text-center">
            <Globe className="w-14 h-14 mx-auto opacity-40 mb-3 text-amber-700" />
            <p className="text-lg font-semibold text-amber-900">{geoLabel(results.origen)} → {geoLabel(results.destino)}</p>
            <p className="text-sm text-amber-800 mt-2">{t("no_route_available")}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold">{geoLabel(results.origen)} → {geoLabel(results.destino)}</h3>
                <p className="text-sm text-slate-500 uppercase tracking-wider mt-1">
                  {results.mode} · {results.tipo_carga.toUpperCase()}
                </p>
              </div>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                <Star className="w-3 h-3" /> {t("meos_route")}
              </span>
            </div>

            <div className="space-y-3">
              {results.resultados.map((r, i) => {
                const merch = getMerchInfo(r);
                const displayedTotal = +(r.precio || 0) + merch.surcharge;
                return (
                <div key={`${r.carrier}-${r.servicio}-${i}`} data-testid={`result-${i}`} className={`bg-white border rounded-xl p-4 ${i === 0 ? "border-red-600 ring-2 ring-red-100" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        {results.tipo_carga === "fcl" ? <Container className="w-5 h-5" /> : <Ship className="w-5 h-5" />}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                          {r.carrier}
                          {r.tarifa_sucursal && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">{t("sucursal_price")}</span>}
                        </div>
                        <div className="text-xs text-slate-500 uppercase tracking-wider">
                          {r.servicio} · {r.dias} días
                          {r.puerto_origen && <span> · {r.puerto_origen} → {r.puerto_destino}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-end">
                        <div className="font-display text-2xl font-bold tabular-nums text-slate-900">{fmtMoney(displayedTotal, r.moneda)}</div>
                        <div className="text-[11px] text-slate-400 uppercase tracking-wider">{r.moneda || "USD"}</div>
                      </div>
                      <button
                        data-testid={`select-${i}`}
                        onClick={() => toast.success(t("saved"))}
                        disabled={merch.missing}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors text-white ${merch.missing ? "bg-slate-300 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                        title={merch.missing ? t("merch_required_hint") : ""}
                      >
                        {t("select_route")}
                      </button>
                      <PdfLangMenu
                        testid={`pdf-${i}`}
                        icon={<Download className="w-4 h-4" />}
                        onPrint={(lg) => generatePDF(r, { origen: geoLabelIn(results.origen, lg), destino: geoLabelIn(results.destino, lg), mode: results.mode, tipo_carga: results.tipo_carga }, merch, lg)}
                        className={merch.missing ? "pointer-events-none opacity-50" : ""}
                      />
                    </div>
                  </div>

                  {merch.list.length > 0 && (
                    <div className={`mt-3 pt-3 border-t rounded-lg -mx-1 px-3 py-2 ${merch.missing ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-slate-50/60"}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                            {t("merchandise")} {merch.required && <span className="text-red-600 ms-1">*</span>}
                          </label>
                          <select
                            data-testid={`merch-sel-${i}`}
                            value={merchSel[r.route_id] || ""}
                            onChange={(e)=>setMerchSel({...merchSel, [r.route_id]: e.target.value})}
                            className={`w-full px-3 py-1.5 border rounded-md text-sm outline-none ${merch.missing ? "border-amber-400 bg-white" : "border-slate-300 bg-white"}`}
                          >
                            <option value="">{merch.required ? t("merch_required_hint") : t("select_option")}</option>
                            {merch.list.map(mt => {
                              const suffix = mt.precio
                                ? ((mt.tipo === "kilo")
                                    ? ` (+${fmtMoney(mt.precio, r.moneda)}/kg)`
                                    : ` (+${fmtMoney(mt.precio, r.moneda)})`)
                                : "";
                              return (
                                <option key={mt.nombre} value={mt.nombre}>
                                  {mt.nombre}{suffix}{mt.is_mandatory ? " *" : ""}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        {merch.selected && merch.surcharge > 0 && (
                          <div className="text-xs">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("merch_surcharge")}</div>
                            <div className="font-semibold tabular-nums text-slate-800">+{fmtMoney(merch.surcharge, r.moneda)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {r.desglose && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{t("price_breakdown")}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <BItem label={t("price_base")} value={fmtMoney(r.desglose.precio_base, r.moneda)} />
                        <BItem label={`${r.desglose.cantidad} × ${fmtMoney(r.desglose.precio_unitario, r.moneda)} / ${r.desglose.unidad}`} value={fmtMoney(r.desglose.subtotal_variable, r.moneda)} />
                        <BItem label={`${t("margin")} (${r.desglose.margen_pct}%)`} value={fmtMoney(r.desglose.margen_monto, r.moneda)} />
                        <BItem label={t("guide_fee")} value={fmtMoney(r.desglose.cargo_guia, r.moneda)} />
                        {r.provincia_surcharge > 0 && (
                          <BItem label={`${t("province_surcharge")} · ${geoLabel(r.provincia_aplicada)}`} value={fmtMoney(r.provincia_surcharge, r.moneda)} />
                        )}
                      </div>
                      {r.desglose.peso_total > 0 && (
                        <div className="mt-2 text-xs text-slate-500">
                          {t("container_weight")}: <b>{r.desglose.peso_contenedor} kg</b> · {t("cargo_weight")}: <b>{r.desglose.peso_mercancia} kg</b> · {t("total_weight_fcl")}: <b>{r.desglose.peso_total.toLocaleString()} kg</b>
                        </div>
                      )}
                    </div>
                  )}

                  {r.observaciones && (
                    <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-600">
                      <span className="font-semibold text-slate-700">{t("observations")}:</span> {r.observaciones}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BItem({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-md p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}
